import { useQuery } from "@tanstack/react-query";

import { preferenceSections } from "../../lib/preferences-form";
import { preferencesQueryOptions, SellerQueryError } from "../../lib/queries/seller";
import { useSellerStore } from "../../lib/store/seller-store";
import { SlidersIcon } from "./icons";

const STATUS = {
  live: { label: "Live", tone: "ok", note: "Mira uses these on every new project." },
  in_review: {
    label: "In review",
    tone: "wait",
    note: "Something you told Mira is waiting for Mira's team.",
  },
  none: {
    label: "Not set",
    tone: "muted",
    note: "Tell Mira what fits you, so every project does.",
  },
} as const;

// The left column of the talent's page: their preferences as they stand right now, facet by
// facet, with one button that opens the full form (the drawer, from the left). Server state
// only (the preferences read the drawer also uses), so a save shows here at once.
export function PreferencesSidebar() {
  const sellerId = useSellerStore((s) => s.activeSellerId);
  const setPrefsOpen = useSellerStore((s) => s.setPrefsOpen);
  const q = useQuery(preferencesQueryOptions(sellerId));
  const status = STATUS[q.data?.status ?? "none"];

  return (
    <aside className="sl-prefs-side" aria-labelledby="sl-prefs-side-title">
      <header className="sl-prefs-side__head">
        <span className="sl-prefs-side__icon" aria-hidden="true">
          <SlidersIcon size={16} />
        </span>
        <h2 id="sl-prefs-side-title" className="sl-prefs-side__title">
          Your preferences
        </h2>
        {q.isPending ? null : (
          <span className={`sl-status sl-status--chip sl-tone--${status.tone}`}>
            {status.label}
          </span>
        )}
      </header>
      {q.isPending ? (
        <div aria-busy="true" aria-label="Loading your preferences">
          {[0, 1, 2].map((i) => (
            <span key={i} className="sl-skel sl-skel--line" />
          ))}
        </div>
      ) : q.isError ? (
        <p className="sl-prefs-side__error" role="alert">
          {q.error instanceof SellerQueryError
            ? q.error.message
            : "Couldn't load your preferences."}{" "}
          <button type="button" className="sl-link" onClick={() => void q.refetch()}>
            Try again
          </button>
        </p>
      ) : (
        <>
          <p className="sl-prefs-side__note">{status.note}</p>
          <dl className="sl-prefs-side__list">
            {preferenceSections(q.data.preferences).map((s) => (
              <div key={s.key} className="sl-prefs-side__row" data-set={s.values.length > 0}>
                <dt className="sl-prefs-side__label">{s.label}</dt>
                {s.values.length > 0 ? (
                  s.values.map((value) => (
                    <dd key={value} className="sl-prefs-side__value">
                      {value}
                    </dd>
                  ))
                ) : (
                  <dd className="sl-prefs-side__value sl-prefs-side__value--unset">{s.unset}</dd>
                )}
              </div>
            ))}
          </dl>
        </>
      )}
      <button
        type="button"
        className="sl-pill-btn sl-prefs-side__edit"
        onClick={() => setPrefsOpen(true)}
        aria-haspopup="dialog"
      >
        Edit preferences
      </button>
    </aside>
  );
}
